import { getApiClient, handleApiResponse, handleApiError } from './client'
import type {
  ApiResponse,
  ApiError,
  Family,
  CreateFamilyRequest,
  UpdateFamilyRequest,
  FamilyMember,
  CreateMemberRequest,
  UpdateMemberRequest,
  FamilyRole
} from './types'

// ============== SERVICIOS DE FAMILIAS ==============

/**
 * Servicio para gestión de familias
 */
export class FamilyApiService {

  /**
   * Obtiene todas las familias del usuario autenticado
   */
  static async getFamilies(): Promise<Family[]> {
    try {
      const client = getApiClient()
      const response = await client.get<ApiResponse<Family[]>>('/families')
      return handleApiResponse(response)
    } catch (error) {
      throw handleApiError(error as ApiError)
    }
  }

  /**
   * Obtiene una familia específica por ID
   */
  static async getFamilyById(id: number): Promise<Family> {
    try {
      const client = getApiClient()
      const response = await client.get<ApiResponse<Family>>(`/families/${id}`)
      return handleApiResponse(response)
    } catch (error) {
      throw handleApiError(error as ApiError)
    }
  }

  /**
   * Crea una nueva familia
   */
  static async createFamily(familyData: CreateFamilyRequest): Promise<Family> {
    try {
      const client = getApiClient()
      const response = await client.post<ApiResponse<Family>>('/families', familyData)
      return handleApiResponse(response)
    } catch (error) {
      throw handleApiError(error as ApiError)
    }
  }

  /**
   * Actualiza una familia existente
   */
  static async updateFamily(id: number, familyData: UpdateFamilyRequest): Promise<Family> {
    try {
      const client = getApiClient()
      const response = await client.put<ApiResponse<Family>>(`/families/${id}`, familyData)
      return handleApiResponse(response)
    } catch (error) {
      throw handleApiError(error as ApiError)
    }
  }

  /**
   * Elimina una familia
   */
  static async deleteFamily(id: number): Promise<void> {
    try {
      const client = getApiClient()
      await client.delete(`/families/${id}`)
    } catch (error) {
      throw handleApiError(error as ApiError)
    }
  }

  /**
   * Obtiene los roles disponibles para familias
   */
  static async getFamilyRoles(): Promise<FamilyRole[]> {
    try {
      const client = getApiClient()
      const response = await client.get<ApiResponse<FamilyRole[]>>('/families/roles')
      return handleApiResponse(response)
    } catch (error) {
      throw handleApiError(error as ApiError)
    }
  }
}

// ============== SERVICIOS DE MIEMBROS ==============

/**
 * Servicio para gestión de miembros de familia
 */
export class MemberApiService {

  /**
   * Obtiene todos los miembros de una familia
   */
  static async getFamilyMembers(familyId: number): Promise<FamilyMember[]> {
    try {
      const client = getApiClient()
      const response = await client.get<ApiResponse<FamilyMember[]>>(`/families/${familyId}/members`)
      return handleApiResponse(response)
    } catch (error) {
      throw handleApiError(error as ApiError)
    }
  }

  /**
   * Obtiene un miembro específico por ID
   */
  static async getMemberById(id: number): Promise<FamilyMember> {
    try {
      const client = getApiClient()
      const response = await client.get<ApiResponse<FamilyMember>>(`/members/${id}`)
      return handleApiResponse(response)
    } catch (error) {
      throw handleApiError(error as ApiError)
    }
  }

  /**
   * Agrega un nuevo miembro a una familia
   */
  static async createFamilyMember(familyId: number, memberData: CreateMemberRequest): Promise<FamilyMember> {
    try {
      const client = getApiClient()
      const response = await client.post<ApiResponse<FamilyMember>>(`/families/${familyId}/members`, memberData)
      return handleApiResponse(response)
    } catch (error) {
      throw handleApiError(error as ApiError)
    }
  }

  /**
   * Actualiza un miembro existente
   */
  static async updateMember(id: number, memberData: UpdateMemberRequest, familyId?: number): Promise<FamilyMember> {
    try {
      const client = getApiClient()
      const response = familyId
        ? await client.patch<ApiResponse<FamilyMember>>(`/families/${familyId}/members/${id}`, memberData)
        : await client.put<ApiResponse<FamilyMember>>(`/members/${id}`, memberData)
      return handleApiResponse(response)
    } catch (error) {
      throw handleApiError(error as ApiError)
    }
  }

  /**
   * Elimina un miembro de la familia
   */
  static async deleteMember(id: number, familyId?: number): Promise<void> {
    try {
      const client = getApiClient()
      const endpoint = familyId ? `/families/${familyId}/members/${id}` : `/members/${id}`
      await client.delete(endpoint)
    } catch (error) {
      throw handleApiError(error as ApiError)
    }
  }
}

// Alias de funciones para compatibilidad
export const getFamilies = FamilyApiService.getFamilies
export const getFamilyById = FamilyApiService.getFamilyById
export const createFamily = FamilyApiService.createFamily
export const updateFamily = FamilyApiService.updateFamily
export const deleteFamily = FamilyApiService.deleteFamily
export const getFamilyRoles = FamilyApiService.getFamilyRoles

export const getFamilyMembers = MemberApiService.getFamilyMembers
export const getMemberById = MemberApiService.getMemberById
export const createFamilyMember = MemberApiService.createFamilyMember
export const updateMember = MemberApiService.updateMember
export const deleteMember = MemberApiService.deleteMember
